import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import moment from "moment";
import CatalogueTopNav from "../new-service/CatalogueTopNav";
import { formService } from "../../_services/form.service";

import "./Forms.css";
import "../new-service/service.css";

/**
 * FormComplete - Page for hairdresser to fill in a form on behalf of a client.
 * Loads the form template, renders interactive inputs per question type,
 * collects responses and submits them via the API.
 */
const FormComplete = (props) => {
  const [template, setTemplate] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successModal, setSuccessModal] = useState(false);

  // Answers state: keyed by sectionId_questionId or sectionId_fieldId
  const [answers, setAnswers] = useState({});
  const [signatureData, setSignatureData] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const canvasRef = useRef(null);
  const lastPointRef = useRef(null);

  const submissionId = props.match?.params?.submissionId;

  useEffect(() => {
    if (submissionId) {
      loadSubmissionAndTemplate();
    } else {
      setError("No submission ID provided");
      setLoading(false);
    }
  }, [submissionId]);

  const loadSubmissionAndTemplate = () => {
    setLoading(true);
    // First load the submission to get formTemplateId
    formService.viewSubmission({ submissionId })
      .then((response) => {
        const data = response?.data?.responseData;
        if (!data?.result) {
          setError("Submission not found");
          setLoading(false);
          return;
        }
        const sub = data.result;
        setSubmission(sub);

        // Now load the full template
        return formService.viewForm({ formId: sub.formTemplateId })
          .then((resp) => {
            const tData = resp?.data?.responseData;
            if (tData?.result) {
              setTemplate(tData.result);
            } else {
              setError("Form template not found");
            }
            setLoading(false);
          });
      })
      .catch((err) => {
        console.error("Error loading form:", err);
        setError("Failed to load form");
        setLoading(false);
      });
  };

  const setAnswer = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const toggleCheckboxAnswer = (key, option) => {
    setAnswers((prev) => {
      const current = prev[key] || [];
      if (current.includes(option)) {
        return { ...prev, [key]: current.filter((o) => o !== option) };
      }
      return { ...prev, [key]: [...current, option] };
    });
  };

  // ===== Signature Pad =====
  const requireSignature = template?.settings?.requireSignature === true;

  const getCanvasPoint = useCallback((e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const startDrawing = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const point = getCanvasPoint(e, canvas);
    lastPointRef.current = point;
    setIsDrawing(true);
  }, [getCanvasPoint]);

  const draw = useCallback((e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const point = getCanvasPoint(e, canvas);
    const last = lastPointRef.current;
    if (last) {
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(point.x, point.y);
      ctx.strokeStyle = "#1a1a1a";
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.stroke();
    }
    lastPointRef.current = point;
  }, [isDrawing, getCanvasPoint]);

  const stopDrawing = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);
    lastPointRef.current = null;
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL("image/png"));
    }
  }, [isDrawing]);

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setSignatureData(null);
  }, []);

  const handleSubmit = () => {
    if (!template || !submission) return;

    // Build responses array from answers
    const responses = [];

    template.sections.forEach((section) => {
      // Client detail fields
      if (section.type === "client_details" && section.fields) {
        section.fields
          .filter((f) => f.enabled)
          .forEach((field) => {
            const key = `${section.id}_${field.id}`;
            if (answers[key] !== undefined && answers[key] !== "") {
              responses.push({
                sectionId: section.id,
                questionId: field.id,
                questionText: field.label,
                questionType: "client_field",
                answer: answers[key],
              });
            }
          });
      }

      // Custom questions
      if (section.questions) {
        section.questions.forEach((question) => {
          const key = `${section.id}_${question.id}`;
          if (answers[key] !== undefined && answers[key] !== "") {
            responses.push({
              sectionId: section.id,
              questionId: question.id,
              questionText: question.question,
              questionType: question.type,
              answer: answers[key],
            });
          }
        });
      }
    });

    if (requireSignature && !signatureData) {
      alert("Please provide a signature before submitting.");
      return;
    }

    setSubmitting(true);
    formService
      .submitForm({
        submissionId: submissionId,
        responses: responses,
        signature: requireSignature ? signatureData : null,
      })
      .then(() => {
        setSuccessModal(true);
        setSubmitting(false);
      })
      .catch((err) => {
        console.error("Error submitting form:", err);
        alert("Failed to submit form. Please try again.");
        setSubmitting(false);
      });
  };

  const goBack = () => {
    if (props.history.length > 1) {
      props.history.goBack();
    } else {
      props.history.push("/forms");
    }
  };

  const handleSuccessClose = () => {
    setSuccessModal(false);
    goBack();
  };

  // ===== Render Helpers =====

  const renderClientDetailField = (section, field) => {
    const key = `${section.id}_${field.id}`;
    return (
      <div key={field.id} style={{ marginBottom: "16px" }}>
        <label style={{ display: "block", fontSize: "13px", fontWeight: "500", color: "#444", marginBottom: "6px" }}>
          {field.label}
          {field.required && <span style={{ color: "#e53935", marginLeft: "4px" }}>*</span>}
        </label>
        {field.id === "address" ? (
          <textarea
            style={textareaStyle}
            rows={3}
            value={answers[key] || ""}
            onChange={(e) => setAnswer(key, e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        ) : (
          <input
            type={field.id === "email" ? "email" : field.id === "birthday" ? "date" : field.id === "mobileNumber" ? "tel" : "text"}
            style={inputStyle}
            value={answers[key] || ""}
            onChange={(e) => setAnswer(key, e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        )}
      </div>
    );
  };

  const renderQuestion = (section, question) => {
    const key = `${section.id}_${question.id}`;
    return (
      <div key={question.id} style={{ marginBottom: "20px" }}>
        <label style={{ display: "block", fontSize: "13px", fontWeight: "500", color: "#444", marginBottom: "6px" }}>
          {question.question || "Untitled question"}
          {question.required && <span style={{ color: "#e53935", marginLeft: "4px" }}>*</span>}
        </label>

        {question.type === "short_answer" && (
          <input
            type="text"
            style={inputStyle}
            value={answers[key] || ""}
            onChange={(e) => setAnswer(key, e.target.value)}
            placeholder="Type your answer"
          />
        )}

        {question.type === "long_answer" && (
          <textarea
            style={textareaStyle}
            rows={4}
            value={answers[key] || ""}
            onChange={(e) => setAnswer(key, e.target.value)}
            placeholder="Type your answer"
          />
        )}

        {question.type === "multiple_choice" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {(question.options || []).map((option, idx) => (
              <label key={idx} style={radioLabelStyle}>
                <input
                  type="radio"
                  name={key}
                  checked={answers[key] === option}
                  onChange={() => setAnswer(key, option)}
                  style={{ marginRight: "10px" }}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}

        {question.type === "checkbox" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {(question.options || []).map((option, idx) => (
              <label key={idx} style={radioLabelStyle}>
                <input
                  type="checkbox"
                  checked={(answers[key] || []).includes(option)}
                  onChange={() => toggleCheckboxAnswer(key, option)}
                  style={{ marginRight: "10px" }}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}

        {question.type === "dropdown" && (
          <select
            style={inputStyle}
            value={answers[key] || ""}
            onChange={(e) => setAnswer(key, e.target.value)}
          >
            <option value="">Select an option</option>
            {(question.options || []).map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        )}

        {question.type === "yes_no" && (
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              onClick={() => setAnswer(key, "Yes")}
              style={{
                ...yesNoStyle,
                background: answers[key] === "Yes" ? "#1a1a1a" : "#fff",
                color: answers[key] === "Yes" ? "#fff" : "#1a1a1a",
              }}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => setAnswer(key, "No")}
              style={{
                ...yesNoStyle,
                background: answers[key] === "No" ? "#1a1a1a" : "#fff",
                color: answers[key] === "No" ? "#fff" : "#1a1a1a",
              }}
            >
              No
            </button>
          </div>
        )}
      </div>
    );
  };

  // ===== Styles =====

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    fontSize: "14px",
    outline: "none",
    background: "#fff",
    boxSizing: "border-box",
  };

  const textareaStyle = {
    ...inputStyle,
    resize: "vertical",
  };

  const radioLabelStyle = {
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    color: "#333",
    cursor: "pointer",
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid #e8e8e8",
    background: "#fafafa",
  };

  const yesNoStyle = {
    padding: "10px 28px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.15s ease",
  };

  return (
    <div className="h_n catalogue-no-header">
      <div className="body_container cm_bg_white">

        <div className="catalogue-page-wrapper">
          <CatalogueTopNav />

          <div className="catalogue-content-container">
            <div className="catalogue-content-wrapper">
              {/* Back button */}
              <button
                onClick={goBack}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "#666",
                  padding: "0",
                  marginBottom: "16px",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Back
              </button>

              {loading ? (
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}>
                  <p>Loading form...</p>
                </div>
              ) : error ? (
                <div style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}>
                  <p>{error}</p>
                </div>
              ) : template ? (
                <div>
                  {/* Hero Image */}
                  {template.heroImage && (
                    <div style={{
                      background: "#fff",
                      borderRadius: "12px",
                      border: "1px solid #e8e8e8",
                      marginBottom: "16px",
                      overflow: "hidden",
                      padding: 0,
                    }}>
                      <img
                        src={template.heroImage}
                        alt=""
                        style={{
                          width: "100%",
                          height: "180px",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </div>
                  )}

                  {/* Form Header */}
                  <div style={{
                    background: "#fff",
                    borderRadius: "12px",
                    border: "1px solid #e8e8e8",
                    padding: "24px",
                    marginBottom: "20px",
                  }}>
                    <h2 style={{ fontSize: "20px", fontWeight: "600", margin: "0 0 4px 0", color: "#1a1a1a" }}>
                      {template.name || "Untitled Form"}
                    </h2>
                    <p style={{ fontSize: "13px", color: "#888", margin: 0 }}>
                      Complete this form on behalf of the client
                    </p>
                  </div>

                  {/* Sections */}
                  {template.sections.map((section, sIndex) => (
                    <div
                      key={section.id || sIndex}
                      style={{
                        background: "#fff",
                        borderRadius: "12px",
                        border: "1px solid #e8e8e8",
                        marginBottom: "16px",
                        overflow: "hidden",
                      }}
                    >
                      {/* Section Header */}
                      <div style={{
                        padding: "16px 24px",
                        borderBottom: "1px solid #f0f0f0",
                        background: "#fafafa",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "22px",
                            height: "22px",
                            borderRadius: "50%",
                            background: section.type === "client_details" ? "#e3f2fd" : "#f3e5f5",
                            color: section.type === "client_details" ? "#1565c0" : "#7b1fa2",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}>
                            {sIndex + 1}
                          </span>
                          <h3 style={{ fontSize: "15px", fontWeight: "600", margin: 0, color: "#1a1a1a" }}>
                            {section.title || "Untitled Section"}
                          </h3>
                        </div>
                        {section.description && (
                          <p style={{ fontSize: "13px", color: "#888", margin: "6px 0 0 30px" }}>
                            {section.description}
                          </p>
                        )}
                      </div>

                      {/* Section Body */}
                      <div style={{ padding: "20px 24px" }}>
                        {/* Client detail fields */}
                        {section.type === "client_details" &&
                          section.fields &&
                          section.fields
                            .filter((f) => f.enabled)
                            .map((field) => renderClientDetailField(section, field))}

                        {/* Custom questions */}
                        {section.questions &&
                          section.questions.map((question) =>
                            renderQuestion(section, question)
                          )}
                      </div>
                    </div>
                  ))}

                  {/* Signature Pad */}
                  {requireSignature && (
                    <div style={{
                      background: "#fff",
                      borderRadius: "12px",
                      border: "1px solid #e8e8e8",
                      marginBottom: "16px",
                      overflow: "hidden",
                    }}>
                      <div style={{
                        padding: "16px 24px",
                        borderBottom: "1px solid #f0f0f0",
                        background: "#fafafa",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: "22px", height: "22px", borderRadius: "50%",
                            background: "#fce4ec", color: "#c62828",
                            fontSize: "11px", fontWeight: "600",
                          }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M12 19V5M5 12l7-7 7 7" />
                            </svg>
                          </span>
                          <h3 style={{ fontSize: "15px", fontWeight: "600", margin: 0, color: "#1a1a1a" }}>
                            Signature
                            <span style={{ color: "#e53935", marginLeft: "4px" }}>*</span>
                          </h3>
                        </div>
                        <p style={{ fontSize: "13px", color: "#888", margin: "6px 0 0 30px" }}>
                          Sign below to confirm the responses
                        </p>
                      </div>
                      <div style={{ padding: "20px 24px" }}>
                        <div style={{
                          position: "relative",
                          border: signatureData ? "2px solid #1a1a1a" : "2px dashed #d0d0d0",
                          borderRadius: "10px",
                          overflow: "hidden",
                          background: "#fafafa",
                          transition: "border-color 0.2s ease",
                        }}>
                          <canvas
                            ref={canvasRef}
                            width={560}
                            height={180}
                            style={{
                              width: "100%",
                              height: "180px",
                              display: "block",
                              cursor: "crosshair",
                              touchAction: "none",
                            }}
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={stopDrawing}
                          />
                          {!signatureData && (
                            <div style={{
                              position: "absolute",
                              top: "50%", left: "50%",
                              transform: "translate(-50%, -50%)",
                              color: "#bbb", fontSize: "13px",
                              pointerEvents: "none", userSelect: "none",
                            }}>
                              Draw signature here
                            </div>
                          )}
                        </div>
                        {signatureData && (
                          <button
                            type="button"
                            onClick={clearSignature}
                            style={{
                              marginTop: "8px",
                              background: "none", border: "none",
                              color: "#888", fontSize: "13px",
                              cursor: "pointer", padding: "4px 0",
                              textDecoration: "underline",
                            }}
                          >
                            Clear signature
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "12px",
                    padding: "20px 0",
                  }}>
                    <button
                      onClick={goBack}
                      style={{
                        padding: "12px 24px",
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0",
                        background: "#fff",
                        fontSize: "14px",
                        fontWeight: "500",
                        cursor: "pointer",
                        color: "#666",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={submitting}
                      style={{
                        padding: "12px 32px",
                        borderRadius: "8px",
                        border: "none",
                        background: submitting ? "#999" : "#1a1a1a",
                        color: "#fff",
                        fontSize: "14px",
                        fontWeight: "500",
                        cursor: submitting ? "not-allowed" : "pointer",
                      }}
                    >
                      {submitting ? "Submitting..." : "Submit Form"}
                    </button>
                  </div>
                </div>
              ) : null}

              {/* Success Modal */}
              {successModal && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: "rgba(0,0,0,0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 9999,
                  }}
                  onClick={handleSuccessClose}
                >
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "16px",
                      padding: "40px",
                      textAlign: "center",
                      maxWidth: "400px",
                      width: "90%",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div style={{
                      width: "56px",
                      height: "56px",
                      borderRadius: "50%",
                      background: "#e6f9f0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px",
                    }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2e7d5b" strokeWidth="2">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 style={{ fontSize: "18px", fontWeight: "600", margin: "0 0 8px", color: "#1a1a1a" }}>
                      Form Submitted
                    </h3>
                    <p style={{ fontSize: "14px", color: "#888", margin: "0 0 20px" }}>
                      The form has been completed successfully.
                    </p>
                    <button
                      onClick={handleSuccessClose}
                      style={{
                        padding: "10px 32px",
                        borderRadius: "8px",
                        border: "none",
                        background: "#1a1a1a",
                        color: "#fff",
                        fontSize: "14px",
                        fontWeight: "500",
                        cursor: "pointer",
                      }}
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    ...state,
  };
}

export default connect(mapStateToProps)(FormComplete);
